import { useState, useEffect, useCallback } from "react";
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { PageTitle, useLayout } from "../../../_metronic/layout/core";
import { DefaultTitle } from "../../../_metronic/layout/components/header/page-title/DefaultTitle";
import { IUsuarios } from "../../interfaces/IUsuarios";
import UsuariosService from "../../services/UsuariosService";
import Swal from 'sweetalert2';
import UsuariosModal from "./UsuariosModal";
import UsuariosTabela from "./UsuariosTabela";
import { IClientes } from "../../interfaces/IClientes";
import ClientesService from "../../services/ClientesService";
import { useAuth } from "../../contexts/AuthProvider";

import '../../../i18n/config';
import { useTranslation } from 'react-i18next';

const PER_PAGE = 20;

type FiltroAtivo = 'todos' | 'ativos' | 'inativos';

function Usuarios() {
    const { usuario } = useAuth();
    const intl = useIntl();
    const { t } = useTranslation();
    const { classes } = useLayout();

    const [usuarios, setUsuarios] = useState<IUsuarios[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [page, setPage] = useState<number>(1);
    const [paginaCarregada, setPaginaCarregada] = useState<boolean>(false);

    const [busca, setBusca] = useState<string>('');
    const [filtroAtivo, setFiltroAtivo] = useState<FiltroAtivo>('todos');

    const [showModal, setShowModal] = useState<boolean>(false);
    const [usuarioVM, setUsuarioVM] = useState<IUsuarios | undefined>(undefined);
    const [modalTitle, setModalTitle] = useState<string>('');
    const [clientes, setClientes] = useState<IClientes[]>([]);

    const ativoParam = filtroAtivo === 'todos' ? null : filtroAtivo === 'ativos';

    const carregarUsuarios = useCallback(async (p: number = page, q: string = busca, ativo: boolean | null = ativoParam) => {
        setPaginaCarregada(false);
        const result = await UsuariosService.listar({ page: p, perPage: PER_PAGE, q: q || undefined, ativo });
        if (result) {
            setUsuarios(result.items);
            setTotal(result.total);
        }
        setPaginaCarregada(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        carregarUsuarios(1, busca, ativoParam);
        setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [busca, filtroAtivo]);

    useEffect(() => {
        if (usuario?.perfil === 'Master') {
            ClientesService.obterTodos(x => setClientes(x));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function toggleModal(loadItens?: boolean): void {
        if (loadItens) {
            carregarUsuarios(page, busca, ativoParam);
        }
        setShowModal(!showModal);
        if (showModal) {
            setTimeout(() => setUsuarioVM(undefined), 300);
        }
    }

    async function alterarStatus(u: IUsuarios) {
        const estaAtivo = u.ativo !== false;
        const acao = estaAtivo ? t('Inativar') : t('Ativar');
        const pergunta = t(`Você realmente deseja ${estaAtivo ? 'inativar' : 'ativar'} esse Usuário?`);

        const swal = Swal.mixin({
            customClass: { confirmButton: 'btn btn-modal-excluir ms-5', cancelButton: 'btn btn-modal-cancelar' },
            buttonsStyling: false
        });
        const result = await swal.fire({
            title: `${acao} ${t('Usuário')}`,
            text: pergunta,
            showCancelButton: true,
            cancelButtonText: t('Cancelar'),
            confirmButtonText: acao,
            reverseButtons: true
        });

        if (!result.isConfirmed) return;

        if (estaAtivo) {
            await UsuariosService.inativar(u.id, () => carregarUsuarios(page, busca, ativoParam));
        } else {
            await UsuariosService.ativar(u.id, () => carregarUsuarios(page, busca, ativoParam));
        }
    }

    async function excluirUsuario(u: IUsuarios) {
        const swal = Swal.mixin({
            customClass: { confirmButton: 'btn btn-modal-excluir ms-5', cancelButton: 'btn btn-modal-cancelar' },
            buttonsStyling: false
        });
        const result = await swal.fire({
            title: t('Excluir Usuário'),
            text: t('Você realmente deseja excluir esse Usuário?'),
            showCancelButton: true,
            cancelButtonText: t('Cancelar'),
            confirmButtonText: t('Excluir'),
            reverseButtons: true
        });

        if (result.isConfirmed) {
            await UsuariosService.excluir(u.id, () => carregarUsuarios(page, busca, ativoParam), t('Usuário excluído com sucesso'));
        }
    }

    function AcoesUsuarios({ item }: { item: IUsuarios }) {
        const podeAlterar = usuario?.perfil === 'Master' || usuario?.perfil === 'Administrador';
        const estaAtivo = item.ativo !== false;
        return (
            <div data-tag="allowRowEvents" className="d-flex align-items-center gap-3">
                {podeAlterar && (
                    <i
                        onClick={() => alterarStatus(item)}
                        title={estaAtivo ? t('Inativar') : t('Ativar')}
                        className={estaAtivo ? 'bi bi-person-x fs-2' : 'bi bi-person-check fs-2'}
                        style={{ color: estaAtivo ? '#f1416c' : '#50cd89', cursor: 'pointer' }}
                    />
                )}
                <i
                    onClick={() => {
                        setModalTitle(t('Editar Usuário'));
                        setUsuarioVM(item);
                        toggleModal();
                    }}
                    className="fas fa-edit fs-2"
                    style={{ color: '#5E6397', cursor: 'pointer' }}
                />
                <i
                    onClick={() => excluirUsuario(item)}
                    className="fas fa-trash-alt fs-2"
                    style={{ color: '#5E6397', cursor: 'pointer' }}
                />
            </div>
        );
    }

    const columns = [
        {
            name: t('Nome'),
            selector: (row: IUsuarios) => row.nome,
            sortable: true,
            wrap: true,
            cell: (row: IUsuarios) => (
                <span style={{ color: row.ativo !== false ? undefined : '#A3A3A3', fontStyle: row.ativo !== false ? undefined : 'italic' }}>
                    {row.nome}
                </span>
            )
        },
        {
            name: t('Email'),
            selector: (row: IUsuarios) => row.email,
            sortable: true,
            wrap: true
        },
        {
            name: t('Nome de Usuário'),
            selector: (row: IUsuarios) => row.nomeUsuario,
            sortable: true,
            wrap: true
        },
        {
            name: t('Perfil'),
            selector: (row: IUsuarios) => row.perfil,
            sortable: true,
            wrap: true
        },
        {
            name: t('Status'),
            cell: (row: IUsuarios) => (
                <span className={`badge ${row.ativo !== false ? 'badge-light-success' : 'badge-light-danger'}`}>
                    {row.ativo !== false ? t('Ativo') : t('Inativo')}
                </span>
            ),
            wrap: true
        },
        {
            name: t('Ações'),
            cell: (row: IUsuarios) => <AcoesUsuarios item={row} />,
            wrap: true
        }
    ];

    const totalPages = Math.ceil(total / PER_PAGE);

    function handlePageChange(newPage: number) {
        setPage(newPage);
        carregarUsuarios(newPage, busca, ativoParam);
    }

    const filtrosAtivo: { label: string; value: FiltroAtivo }[] = [
        { label: t('Todos'), value: 'todos' },
        { label: t('Ativos'), value: 'ativos' },
        { label: t('Inativos'), value: 'inativos' }
    ];

    return <>
        <PageTitle breadcrumbs={[]}>{intl.formatMessage({ id: t('MENU.USERS') })}</PageTitle>

        <div className='toolbar' id='kt_toolbar'>
            <div id='kt_toolbar_container' className={clsx(classes.toolbarContainer.join(' '), 'd-flex flex-stack')}>
                <DefaultTitle />
                <UsuariosModal clientes={clientes} usuario={usuarioVM} title={modalTitle} show={showModal} toggleModal={toggleModal} />
                <div className='d-flex align-items-center py-1'>
                    <button
                        onClick={() => {
                            setModalTitle(t('Cadastrar Usuário'));
                            setUsuarioVM(undefined);
                            setShowModal(true);
                        }}
                        className='btn btn-sm btn-primario'
                    >
                        <i className="icon-lg text-white fas fa-plus" />
                        <span style={{ verticalAlign: 'middle' }}> {t('USUÁRIO')} </span>
                    </button>
                </div>
            </div>
        </div>

        <div className="row g-3 mb-4">
            <div className="col-12 col-md-6">
                <input
                    type='text'
                    className="form-control"
                    placeholder={t('Filtrar por nome, e-mail ou nome de usuário')}
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                />
            </div>
            <div className="col-12 col-md-auto d-flex gap-2 align-items-center">
                {filtrosAtivo.map(f => (
                    <button
                        key={f.value}
                        onClick={() => setFiltroAtivo(f.value)}
                        className={`btn btn-sm ${filtroAtivo === f.value ? 'btn-primario' : 'btn-outline btn-outline-secondary'}`}
                    >
                        {f.label}
                    </button>
                ))}
            </div>
        </div>

        {paginaCarregada ? (
            <>
                <UsuariosTabela colunas={columns} itens={usuarios} />
                {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-4">
                        <span className="text-muted fs-6">
                            {t('Mostrando')} {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, total)} {t('de')} {total} {t('usuários')}
                        </span>
                        <div className="d-flex gap-2">
                            <button
                                className="btn btn-sm btn-outline btn-outline-secondary"
                                disabled={page === 1}
                                onClick={() => handlePageChange(page - 1)}
                            >
                                ‹ {t('Anterior')}
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                                .reduce<(number | string)[]>((acc, p, idx, arr) => {
                                    if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('...');
                                    acc.push(p);
                                    return acc;
                                }, [])
                                .map((p, idx) =>
                                    p === '...' ? (
                                        <span key={`ellipsis-${idx}`} className="btn btn-sm disabled">…</span>
                                    ) : (
                                        <button
                                            key={p}
                                            className={`btn btn-sm ${page === p ? 'btn-primario' : 'btn-outline btn-outline-secondary'}`}
                                            onClick={() => handlePageChange(p as number)}
                                        >
                                            {p}
                                        </button>
                                    )
                                )}
                            <button
                                className="btn btn-sm btn-outline btn-outline-secondary"
                                disabled={page === totalPages}
                                onClick={() => handlePageChange(page + 1)}
                            >
                                {t('Próximo')} ›
                            </button>
                        </div>
                    </div>
                )}
            </>
        ) : (
            <p>{t('Carregando...')}</p>
        )}
    </>;
}

export default Usuarios;
